import type { MetricsOverviewDto, RevenueByPlanDto } from '@planuze/admin-contracts';
import { useMemo, useState } from 'react';
import { useMetricsWidgetsContent } from '../../../content/sections/metrics-widgets.js';
import type { Currency } from '../../../lib/format.js';
import { FacetMultiSelect } from '../../filters/facet-multi-select.js';
import { MetricCardsGrid } from '../metric-cards-grid.js';
import { RevenueByPlanChart } from '../revenue-by-plan-chart.js';

interface Props {
  readonly overview: MetricsOverviewDto | undefined;
  readonly revenue: RevenueByPlanDto | undefined;
  readonly loading: boolean;
  readonly currency: Currency;
}

/* Aba Receita: cards de MRR/churn (moeda page-level) + donut de receita por plano.
   Filtro de plano client-side sobre `items[].plan` (campo que já existe no DTO). */
export function RevenueTab({ overview, revenue, loading, currency }: Props) {
  const copy = useMetricsWidgetsContent().planFilter;
  const [plans, setPlans] = useState<string[]>([]);
  const items = revenue?.items ?? [];
  const planOptions = useMemo(
    () => items.map((item) => ({ value: item.plan, label: item.plan })),
    [items],
  );
  const filtered = useMemo(
    () => (plans.length === 0 ? items : items.filter((item) => plans.includes(item.plan))),
    [items, plans],
  );
  const clearFilters = (): void => setPlans([]);
  const filteredData = revenue === undefined ? undefined : { ...revenue, items: filtered };

  return (
    <div className="space-y-4">
      <MetricCardsGrid overview={overview} section="revenue" currency={currency} />
      <div className="flex flex-wrap items-center gap-3">
        <FacetMultiSelect
          label={copy.label}
          values={plans}
          onChange={setPlans}
          options={planOptions}
        />
        {plans.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {copy.resultCount(filtered.length, items.length)}
          </span>
        )}
      </div>
      <RevenueByPlanChart
        data={filteredData}
        loading={loading}
        currency={currency}
        hasFilters={plans.length > 0}
        onClearFilters={clearFilters}
      />
    </div>
  );
}
